/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { DeleteOutlined } from '@ant-design/icons'

import Text from '../Text'
import ImageIcon from 'src/components/icons/ImageIcon'
import {
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGE_SIZE_MB,
} from '@sportninja/common/constants/imageRequirements'
import { message } from 'antd'

type ImageUploaderProps = {
  label: string
  tip: string
  imageUrl: string
  setImageUrl: (url: string) => void
  messageApi?: any
  setImageChanged?: (changed: boolean) => void
}

const getBase64 = (
  img: File,
  callback: (url: string | ArrayBuffer | null) => void
) => {
  const reader = new FileReader()
  reader.addEventListener('load', () => callback(reader.result))
  if (img) {
    reader.readAsDataURL(img)
  }
}

const ImageUploader = ({
  label,
  tip,
  imageUrl,
  setImageUrl,
  messageApi,
  setImageChanged = () => {},
}: ImageUploaderProps) => {
  const [isLoading, setIsLoading] = useState(false)

  const beforeUpload = (file: File): boolean => {
    const isAllowedType = ALLOWED_IMAGE_TYPES.includes(file.type)
    const isLtMaxSize = file.size / 1024 / 1024 <= MAX_IMAGE_SIZE_MB
    if (!isAllowedType) {
      messageApi?.open({
        type: 'error',
        content:
          'Please upload a file in one of the following formats: JPEG, GIF, PNG, AVIF, WebP, TIFF, or HEIC.',
        duration: 5,
      })
      return false
    }
    if (!isLtMaxSize) {
      message?.open({
        type: 'error',
        content: 'File is too large. Please upload an image smaller than 5MB.',
        duration: 5,
      })
      return false
    }
    return true
  }

  // Fix: define handleFileChange and fix render logic
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0]
    if (!file) {
      return
    }

    if (!beforeUpload(file)) {
      // Reset input value so user can re-upload the same file if needed
      e.target.value = ''
      return
    }

    setIsLoading(true)
    getBase64(file, (url) => {
      setIsLoading(false)
      if (typeof url === 'string') {
        setImageUrl(url)
        setImageChanged(true)
      }
      // Reset input value so user can re-upload the same file if needed
      e.target.value = ''
    })
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        align-self: stretch;
        width: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 4px;
        `}
      >
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          {label}
        </Text>
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_100}
        >
          {tip}
        </Text>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
          width: 100%;
        `}
      >
        <div
          css={css`
            display: flex;
            padding: 8px;
            justify-content: ${imageUrl ? 'flex-start' : 'space-between'};
            align-items: center;
            gap: ${imageUrl ? '8px' : '0'};
            flex: 1 0 0;
            border-radius: 8px;
            border: 1px solid ${colors.SECONDARY_100};
            background: ${colors.SECONDARY_200};
            width: 100%;
            align-self: stretch;
            cursor: ${isLoading ? 'not-allowed' : 'pointer'};
            position: relative;
            ${isLoading &&
            css`
              opacity: 0.7;
            `}
          `}
        >
          <input
            type='file'
            accept='image/jpeg,image/png,image/gif,image/avif,image/webp,image/tiff,image/heic,image/bmp,image/jp2'
            onChange={handleFileChange}
            disabled={isLoading}
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              opacity: 0;
              cursor: ${isLoading ? 'not-allowed' : 'pointer'};
              z-index: 1;
            `}
          />
          {imageUrl ? (
            <>
              <div
                css={css`
                  width: 40px;
                  height: 40px;
                  border-radius: 4px;
                  overflow: hidden;
                  flex-shrink: 0;
                `}
              >
                <img
                  src={imageUrl}
                  alt='Uploaded image'
                  css={css`
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                  `}
                  onError={(e) => {
                    e.currentTarget.src =
                      '/images/generic_placeholders/placeholder_image.png'
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_100}
              >
                Select an Image
              </Text>
              <ImageIcon />
            </>
          )}
        </div>
        {imageUrl && (
          <DeleteOutlined
            css={css`
              font-size: 24px;
              color: ${colors.ERROR_LIGHT};
              cursor: pointer;
            `}
            onClick={() => {
              setImageUrl('')
              setImageChanged(true)
            }}
          />
        )}
      </div>
      <div
        css={css`
          margin-top: 0;
          text-align: right;
          align-self: flex-end;
          display: none;
        `}
      >
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_100}
        >
          Supported formats: JPEG, GIF, PNG, AVIF, Bitmap, WebP, TIFF, JPEG
          2000, and HEIC. Maximum file size: 5MB.
        </Text>
      </div>
    </div>
  )
}

export default ImageUploader
